/**
 * Display formatting for the phone number printed on the card.
 *
 * Entra holds numbers in whatever shape the local admin typed: Timo's mobile
 * is E.164 ("+491723880120"), his desk line already has spaces. The card
 * template (Brand Guidelines 3.0, p.47) groups the digits, "M: + XX XXXXX
 * XXXXXX", so a bare E.164 value is split into country code and national
 * number, the latter grouped by the convention of that country. Values that
 * already carry spaces, dashes or brackets are left exactly as entered.
 *
 * The vCard keeps the raw value; only the printed line is formatted.
 */

/** Calling codes that are one digit; two-digit codes are listed; the rest are three. */
const ONE_DIGIT_CODES = new Set(["1", "7"]);
const TWO_DIGIT_CODES = new Set([
  "20",
  "27",
  "30",
  "31",
  "32",
  "33",
  "34",
  "36",
  "39",
  "40",
  "41",
  "43",
  "44",
  "45",
  "46",
  "47",
  "48",
  "49",
  "51",
  "52",
  "53",
  "54",
  "55",
  "56",
  "57",
  "58",
  "60",
  "61",
  "62",
  "63",
  "64",
  "65",
  "66",
  "81",
  "82",
  "84",
  "86",
  "90",
  "91",
  "92",
  "93",
  "94",
  "95",
  "98",
]);

/**
 * How the national number is grouped, per calling code, for the countries
 * Norican has sites in. The last group takes whatever digits remain. Anything
 * not listed gets "prefix + subscriber" (3 + rest), the DIN 5008 shape.
 */
const NATIONAL_GROUPS: Record<string, number[]> = {
  "1": [3, 3], // +1 212 555 0100
  "33": [1, 2, 2, 2], // +33 6 12 34 56 78
  "34": [3, 2, 2], // +34 612 34 56 78
  "44": [4], // +44 7900 666365
  "45": [2, 2, 2], // +45 12 34 56 78
  "81": [2, 4], // +81 90 1234 5678
  "86": [3, 4], // +86 138 1234 5678
  "91": [5], // +91 98765 43210
  "420": [3, 3], // +420 601 123 456
};

const E164 = /^\+\d{7,15}$/;

/** "+491723880120" → "+49 172 3880120"; anything already formatted is returned untouched. */
export function formatPhone(raw: string): string {
  const value = raw.trim().replace(/\s+/g, " ");
  if (!E164.test(value)) {
    return value;
  }

  const digits = value.slice(1);
  const codeLength = ONE_DIGIT_CODES.has(digits[0] ?? "")
    ? 1
    : TWO_DIGIT_CODES.has(digits.slice(0, 2))
      ? 2
      : 3;
  const code = digits.slice(0, codeLength);
  let national = digits.slice(codeLength);
  if (!national) {
    return value;
  }

  const groups: string[] = [];
  for (const size of NATIONAL_GROUPS[code] ?? [3]) {
    if (national.length <= size) {
      break;
    }
    groups.push(national.slice(0, size));
    national = national.slice(size);
  }
  groups.push(national);
  return `+${code} ${groups.join(" ")}`;
}
